import Swal from "sweetalert2";
import "../pages/game/stageHelp.css";
import { getThemeButtonGradient, getThemeButtonTextColor } from "./themeAssets";

export const STAGE_RULES_KEYS = {
  2: "unscramble_rules",
};

export const DEFAULT_STAGE_RULES = {
  unscramble_rules: [
    "Read the scrambled phrase on the left.",
    "Type your answer in the box on the right.",
    "Solve the minimum number of words, then tap Submit.",
  ],
};

export const STAGE_RULES_LABELS = {
  unscramble_rules: "Unscramble",
};

export function ensureRulesArray(val) {
  if (Array.isArray(val)) {
    return val.map((r) => String(r ?? "").trim()).filter(Boolean);
  }
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) {
        return parsed.map((r) => String(r ?? "").trim()).filter(Boolean);
      }
    } catch {
      const t = val.trim();
      return t ? [t] : [];
    }
    const t = val.trim();
    return t ? [t] : [];
  }
  return [];
}

export function getStageRulesFromTheme(themeData, rulesKey) {
  const configured = ensureRulesArray(themeData?.[rulesKey]);
  if (configured.length) return configured;

  return [...(DEFAULT_STAGE_RULES[rulesKey] || [])];
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatRulesAsHelpHtml(rules, title = "How to play") {
  const list = ensureRulesArray(rules);
  const steps = list.length
    ? list
        .map(
          (rule, i) => `<li class="stage-help-step">
            <span class="stage-help-step__num" aria-hidden="true">${i + 1}</span>
            <span class="stage-help-step__text">${escapeHtml(rule)}</span>
          </li>`
        )
        .join("")
    : `<li class="stage-help-step">
        <span class="stage-help-step__num" aria-hidden="true">1</span>
        <span class="stage-help-step__text">No rules configured for this stage.</span>
      </li>`;

  return `<div class="stage-help-card">
    <div class="stage-help-card__hero">
      <h2 class="stage-help-card__title">${escapeHtml(title)}</h2>
      <p class="stage-help-card__subtitle">Follow these steps to complete this stage</p>
    </div>
    <ul class="stage-help-steps">${steps}</ul>
    <div class="stage-help-card__footer">
      <button type="button" class="stage-help-got-it-btn" data-stage-help-close>Got it!</button>
    </div>
  </div>`;
}

function resolveHelpButtonGradient(themeData, fallbackStart = "#2f9e4f", fallbackEnd = "#248a42") {
  const { start, end } = getThemeButtonGradient(themeData);
  return {
    start: start || fallbackStart,
    end: end || fallbackEnd,
  };
}

const STAGE_HELP_MODAL_CLASS = "stage-help-modal-open";
const STAGE_HELP_BACKDROP_ID = "stage-help-backdrop";

let helpModalScrollY = 0;

function mountStageHelpBackdrop() {
  if (document.getElementById(STAGE_HELP_BACKDROP_ID)) return;
  const backdrop = document.createElement("div");
  backdrop.id = STAGE_HELP_BACKDROP_ID;
  backdrop.className = "stage-help-backdrop";
  backdrop.setAttribute("aria-hidden", "true");
  document.body.appendChild(backdrop);
}

function unmountStageHelpBackdrop() {
  document.getElementById(STAGE_HELP_BACKDROP_ID)?.remove();
}

export function lockPageScrollForStageModal() {
  helpModalScrollY =
    window.scrollY || document.documentElement.scrollTop || 0;
  document.documentElement.classList.add(STAGE_HELP_MODAL_CLASS);
  document.documentElement.style.overflow = "hidden";
  document.body.style.overflow = "hidden";
  document.body.style.position = "fixed";
  document.body.style.top = `-${helpModalScrollY}px`;
  document.body.style.left = "0";
  document.body.style.right = "0";
  document.body.style.width = "100%";
  mountStageHelpBackdrop();
}

export function unlockPageScrollForStageModal() {
  document.documentElement.classList.remove(STAGE_HELP_MODAL_CLASS);
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  unmountStageHelpBackdrop();
  window.scrollTo(0, helpModalScrollY);
}

export function fireStageHelpSwal({
  rules,
  title = "How to play",
  themeData,
  confirmButtonColor,
}) {
  lockPageScrollForStageModal();

  const { start: btnStart, end: btnEnd } = resolveHelpButtonGradient(themeData);

  Swal.fire({
    html: formatRulesAsHelpHtml(rules, title),
    showConfirmButton: false,
    showCloseButton: false,
    position: "center",
    width: "min(92vw, 26rem)",
    heightAuto: true,
    scrollbarPadding: false,
    returnFocus: false,
    customClass: {
      popup: "stage-help-popup",
      htmlContainer: "stage-help-popup__html",
    },
    showClass: { popup: "stage-help-popup--in" },
    didOpen: () => {
      const btn = Swal.getPopup()?.querySelector("[data-stage-help-close]");
      if (btn) {
        const start = confirmButtonColor || btnStart;
        const end = confirmButtonColor
          ? confirmButtonColor
          : btnEnd;
        btn.style.background = `linear-gradient(135deg, ${start}, ${end})`;
        const textColor = getThemeButtonTextColor(themeData);
        if (textColor) btn.style.color = textColor;
        btn.addEventListener("click", () => Swal.close(), { once: true });
      }
    },
    didClose: () => {
      unlockPageScrollForStageModal();
    },
  });
}

export function getAllStageRulesSections(themeData) {
  return Object.entries(STAGE_RULES_KEYS).map(([, key]) => ({
    key,
    label: STAGE_RULES_LABELS[key] || key,
    rules: getStageRulesFromTheme(themeData, key),
  }));
}
